import React, { Component, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { OutsideClick } from "outsideclick-react";
import { Image } from 'components/Image/Image';
import circleProfile from 'assets/img/icons/circle_profile.png';
import { connect } from 'react-redux';
import ClipboardService from 'services/ClipboardService';
import { pushToastMessage } from 'store/actions/root/toast';
import { updateMessagePopup } from 'store/actions/root/message-popup';
import { fetchCurrentUserData, receiveCurrentUserData } from 'store/actions/root/user';
import { setRequestStatus, } from 'store/actions/user/companies';
import ScrollToTop from 'components/Commons/ScrollToTop/ScrollToTop';
import { getParams } from 'util.jsx';
import { withResearchContext, authHeader } from './ResearchContext.provider';
import ChatStreamView from './components/ChatStreamView/ChatStreamView.component';
import { mdToHTML } from 'utils/MD.utils';
import { FollowUpQuestionList } from './components/FollowUpQuestionList/FollowUpQuestionList.component';
import ResearchNavBar from './components/ResearchNavBar/ResearchNavBar.component';
import { reportMap } from './strings';
import './Research.scss';

const HOST = process.env.REACT_APP_SERVER;

export class Research extends Component {

  constructor(props) {
    super(props);
    this.state = {
      availableModels: [],
      modelsLoading: false,
      statusMessage: null,
      dateStatusMessage: null,
    };
    this.chatRef = React.createRef();
    this.selectRef = React.createRef();
    this.inputRef = React.createRef();
  }

  fetchAvailableModels = async () => {
    this.setState({ modelsLoading: true });
    try {
      const response = await fetch(`${HOST}/api/nlp/models`);
      if (response.ok) {
        const data = await response.json();
        this.setState({ availableModels: data.models || [] });
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      this.setState({ modelsLoading: false });
    }
  }

  UNSAFE_componentWillMount() {
    this.props.fetchCurrentUserData();
  }

  componentDidMount() {
    // Disable body scroll
    document.body.classList.add('research-page');
    document.documentElement.classList.add('research-page');

    // Fetch available models for the debug dropdown (only when debug mode is enabled)
    const params = getParams(this.props.location);
    if (params.has('debug')) {
      this.fetchAvailableModels();
    }

    const reportType = params.has('t') && params.get('t');
    const shareId = params.has('s') && params.get('s');
    if (shareId) {
      this.loadChat(shareId);
    } else if (reportType) {
      this.props.researchContext.actions.setMultiple({
        longReportType: reportType
      });
      if (reportType in reportMap)
        document.title = `Enki - ${reportMap[reportType]}`;
    }
    const callFollowUpQuestions = (reportType) => {
      return this.onExpandResearchClick(reportType);
    };

    const callSendUserMessage = () => {
      return this.sendUserMessage();
    };
    this.props.researchContext.actions.setOnExpandResearchClick(() => callFollowUpQuestions);
    this.props.researchContext.actions.setSendUserMessage(() => callSendUserMessage);
    const { reportIntroSteps, REPORT_INTRO_COOKIE_KEY } = require('./reportIntro.steps');
    const { startIntroTour } = require('hooks/useIntroTour');
    this._introPromise = startIntroTour(reportIntroSteps, { timeout: 3000, cookieKey: REPORT_INTRO_COOKIE_KEY, cookieExpireDays: 365 });
    this.resizeObserver = null;
  }

  scrollToLatestMessage = () => {
    if (this.chatRef.current) {
      this.chatRef.current.scrollTo({
        top: this.chatRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    // window.scrollTo is unnecessary because body scroll is disabled
  }

  UNSAFE_componentWillReceiveProps(props) {
    const params = getParams(this.props.location);
    const timestamp = params.has('c') && params.get('c');
    if (timestamp) {
      this.props.researchContext.actions.setMultiple({
        timestamp: timestamp
      });
      this.loadChat();
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Re-enable body scroll
    document.body.classList.remove('research-page');
    document.documentElement.classList.remove('research-page');

    try {
      if (this._introPromise && typeof this._introPromise.then === 'function') {
        this._introPromise.then((intro) => {
          if (intro && typeof intro.exit === 'function') intro.exit();
        }).catch(() => { });
      }
    } catch (e) { }
  }

  setMultipleUpdate(updates) {
    return this.props.researchContext.actions.setMultiple(updates);
  }

  calculateReportQuality() {
    const { state } = this.props.researchContext;
    const { chat } = state;
    const lastMessage = chat[chat.length - 1];
    const report = lastMessage.content.replace(/<a\b[^>]*>(.*?)<\/a>/gi, "")

    // match numbers, including integers, decimals
    const numbers = report.match(/\b\d+(?:\.\d+)?(?![.)])\b/g) || [];

    // match common currency symbols
    const currencySybols = report.match(/[\$\€\£\¥\₹\₽\₩\฿\¢]/g) || [];

    // match percent sign
    const percentChars = report.match(/%/g) || [];

    const score = numbers.length + currencySybols.length + percentChars.length;
    const paragraphCounts = report.split("\n").filter(i => i.length > 10).length;

    lastMessage.content += `<br><br><span style="background-color:red; border-radius: 25px; padding: 8px 8px; color: white;">${(score / paragraphCounts).toFixed(2)}</span>`;
    this.setMultipleUpdate({ chat });
  }

  loadChat = (shareId) => {
    this.props.researchContext.actions.loadChat(shareId);
  }

  onKeyUp = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const { streaming } = this.props.researchContext.state;
      if (streaming) {
        this.onStopClick()
      } else {
        this.sendUserMessage();
      }
    }
  }

  onStopClick = () => {
    this.setMultipleUpdate({ stop: true, typing: false, expandingTyping: false, streaming: false, expandResearch: false });
    try {
      this.controller && this.controller.abort();
    } catch (e) {
      // AbortError is expected when stopping, suppress it
    }
  }

  onFollowUpClick = (e) => {
    const message = e.target.textContent;
    this.props.researchContext.actions.setChatMessage(message);
    this.setMultipleUpdate({ autocomplete: false });
  }

  copyTextForSafari = (text) => {
    const toastData = {
      title: "Click 'Copy' below to get the share link.",
      iconName: "done",
      iconColor: "blue-2",
      duration: 10000,
      buttons: [
        {
          text: "Copy",
          icon: "content_copy",
          color: "blue",
          onClick: () => {
            new ClipboardService(document).copyText(text);
            this.showCopiedTextMessage();
          }
        }
      ]
    };
    this.props.pushToastMessage(toastData);
  }

  onInputChange = (event) => {
    const value = event.target.value;
    this.props.researchContext.actions.setMultiple({ message: value, autocomplete: true, latestMessage: value, showEnhanceOptions: false });
    this.props.researchContext.actions.setChatMessage(value);
  }

  onTextAreaChange = (state) => (e) => {
    this.props.researchContext.actions.setMultiple({ [state]: e.target.value });
  }

  onDataCheckBoxChange = (checkbox) => {
    return (e) => {
      const { myDataCheckBox, liteDataCheckBox, modelDataCheckBox } = this.state;

      myDataCheckBox.value = false;
      liteDataCheckBox.value = false;
      modelDataCheckBox.value = false;
      checkbox.value = true;

      this.setMultipleUpdate({ myDataCheckBox, liteDataCheckBox, modelDataCheckBox });
    }
  }

  onLengthCheckBoxChange = (checkbox) => {
    return (e) => {
      // const { longReportCheckBox, shortReportCheckBox, customReportCheckBox } = this.state;
      // longReportCheckBox.value = false;
      // shortReportCheckBox.value = false;
      // customReportCheckBox.value = false;
      checkbox.value = !checkbox.value;
      this.setMultipleUpdate({ checkbox });
    }
  }

  onSelectChange = (state) => ({ target }) => {
    this.setMultipleUpdate({ [state]: target.value });
  }

  onOutsideClick = (e) => {
    this.setMultipleUpdate({ autocomplete: false, showConversationHistory: false });
  }

  onAutocompleteClick = (e) => {
    const message = e.target.textContent;
    this.props.researchContext.actions.setChatMessage(message);
  }

  onEnhanceClick = (e) => {
    const { message, enhanceLoading } = this.props.researchContext.state;
    if (!message || message.trim().length === 0 || enhanceLoading) return;
    this.setMultipleUpdate({ autocomplete: false });
    this.props.researchContext.actions.fetchEnhanceSuggestions(message);
  }

  onEnhanceOutsideClick = () => {
    this.setMultipleUpdate({ showEnhanceOptions: false });
  }

  onEnhanceOptionClick = (e) => {
    const message = e.target.textContent;
    // Deliberately not setChatMessage: it also reopens the autocomplete
    // dropdown and fires a debounced type-ahead fetch, which would undo
    // the pick the user just made.
    this.setMultipleUpdate({ message, latestMessage: message, showEnhanceOptions: false });
  }

  onConversationHistoryClick = (e) => {
    this.props.researchContext.actions.setMultiple({ showConversationHistory: true });
    this.props.researchContext.actions.getConversationHistory();
  }

  onLoadConversationClick = (e) => {
    const timestamp = e.target.id;
    this.controller && this.controller.abort();
    this.props.researchContext.actions.setMultiple({ timestamp, showConversationHistory: false });
    this.props.researchContext.actions.loadChat(null, timestamp);
  }

  onDeleteConversationClick = (e) => {
    const timestamp = e.target.id;
    this.props.researchContext.actions.deleteConversation(timestamp);
  }

  sendUserMessage = () => {
    const { chat, latestMessage } = this.props.researchContext.state;
    const { setMultiple } = this.props.researchContext.actions;
    const currentLatestMessage = latestMessage;

    // Abort any still-running previous request so it can't race with this
    // new one and leave `streaming`/`typing` stuck from a stale response.
    this.controller && this.controller.abort();

    setMultiple({
      typing: true,
      chat: [...chat, { role: "user", content: currentLatestMessage }],
      followUpQuestions: {},
      autocomplete: false,
      showEnhanceOptions: false,
      showMoreClicked: false,
      diveDeeperClicked: false
    });
    this.getAnswer(currentLatestMessage, [...chat, { role: "user", content: currentLatestMessage }], false, false, 0);
    setMultiple({ message: "" });
  };

  onExpandResearchClick = (researchType) => {
    const { chat } = this.props.researchContext.state;
    const {
      setDiveDeeperClicked,
      setTyping,
      setExpandingTyping,
      setFollowUpQuestions,
      setChat,
      setShowMoreClicked
    } = this.props.researchContext.actions;
    this.controller && this.controller.abort();

    const lastUserMessage = chat.slice().reverse().find((msg) => msg.role === "user");
    const message = lastUserMessage?.content || "";

    setTyping(true);
    setExpandingTyping(true);
    setFollowUpQuestions({});
    const newChat = chat.slice(0, -1);
    newChat.sources = [];
    setChat(newChat);

    if (researchType === "diveDeeper") {
      setDiveDeeperClicked(true);
    } else if (researchType === "showMore") {
      setShowMoreClicked(true);
    }

    if (researchType === "diveDeeper") {
      this.getAnswer(message, newChat, true, false, 0);
    } else if (researchType === "showMore") {
      this.getAnswer(message, newChat, false, true, 0);
    }
  };

  getAnswer = async (message, chatMessage, expandResearch, showMore, retry) => {
    const { setTyping, setStreaming, setExpandingTyping, setChat, setRouterReportType, setMultiple } = this.props.researchContext.actions;
    const { chat,
      stop,
      myDataCheckBox,
      liteDataCheckBox,
      customReportCheckBox,
      modelDataCheckBox,
      longReportType,
      overrideReportTypeForRequest,
      systemPrompt,
      model,
      startMyDataDate,
      endMyDataDate,
      startLiteDataDate, endLiteDataDate,
      userDateOverride,
      reportPrompt, keywordPrompt,
      previousMainQuestion
    } = this.props.researchContext.state;
    if (retry == 2) {
      setTyping(false);
      setStreaming(false);
      setExpandingTyping(false);
      setChat([...chat, { role: "assistant", content: "Sorry, I don't have enough data to answer." }]);
      return;
    }

    this.controller = new AbortController();
    setStreaming(true);
    this.setState({ statusMessage: null });
    // this.addImages(chat);

    // Use overrideReportTypeForRequest if set, otherwise use longReportType
    const effectiveReportType = overrideReportTypeForRequest || longReportType;

    const body = {
      chat: chatMessage,
      myReportsData: myDataCheckBox.value,
      liteData: liteDataCheckBox.value,
      modelData: modelDataCheckBox.value,
      longReport: effectiveReportType != "shortReport",
      longReportType: effectiveReportType,
      shortReport: effectiveReportType == "shortReport",
      customReport: customReportCheckBox.value,
      myDataDate: [startMyDataDate, endMyDataDate],
      liteDataDate: [startLiteDataDate, endLiteDataDate],
      userDateOverride,
      expandResearch: expandResearch,
      showMore: showMore,
      systemPrompt: systemPrompt,
      reportPrompt: reportPrompt,
      keywordPrompt: keywordPrompt,
      model: model,
      debug: getParams(this.props.location).has('debug'),
      user: this.props.currentUserData.email,
      // Send previousMainQuestion from state for follow-up question tracking
      previousMainQuestion: previousMainQuestion || null
    };
    try {
      const response = await fetch(`${HOST}/api/nlp/chatStream`, {
        method: 'POST',
        signal: this.controller.signal,
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Fetch failed with status ${response.status}: ${errorText}`);
      }

      const routerData = JSON.parse(decodeURIComponent(response.headers.get('X-Router-Data')));
      setRouterReportType(routerData.label);

      // Store main_question from router for next request's previousMainQuestion
      // This ensures the chain propagates correctly:
      // Q1 (new) -> main_question = Q1 -> previousMainQuestion = Q1
      // Q2 (follow-up) -> main_question = Q1 (inherited) -> previousMainQuestion = Q1
      // Q3 (follow-up) -> main_question = Q1 (from previousMainQuestion) -> previousMainQuestion = Q1
      if (routerData.main_question) {
        setMultiple({ previousMainQuestion: routerData.main_question });
      }

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
      let text = "";
      let insideDateMarker = false;
      while (true) {
        const { value, done } = await reader.read();

        const message = value;
        const { chat } = this.props.researchContext.state;
        const { setChat } = this.props.researchContext.actions;
        const messages = chat;
        const recentMessage = messages[chat.length - 1];

        // Parse router data marker
        const routerMatch = value && value.match(/__ROUTER_JSON_START__(.*?)__ROUTER_JSON_END__/s);
        if (routerMatch) {
          try {
            const routerData = JSON.parse(routerMatch[1]);
            setRouterReportType(routerData.label);
            if (routerData.main_question) {
              setMultiple({ previousMainQuestion: routerData.main_question });
            }
          } catch (error) {
            console.error('Error parsing router JSON:', error);
          }
        }

        // Extract markers from message and parse them
        const sourcesMatch = value && value.match(/_{2,3}SOURCES_JSON_START__(.*?)__SOURCES_JSON_END_{0,3}/s);
        if (sourcesMatch) {
          try {
            const questionChat = messages[chat.length - 2];
            const sourcesData = JSON.parse(sourcesMatch[1]);
            questionChat.sources = sourcesData;
            setChat([...chat]);
          } catch (error) {
            console.error('Error parsing sources JSON:', error);
          }
        }

        // Parse URL statistics marker
        const urlStatsMatch = value && value.match(/_{2,3}URL_STATS_JSON_START__(.*?)__URL_STATS_JSON_END_{0,3}/s);
        if (urlStatsMatch) {
          try {
            const questionChat = messages[chat.length - 2];
            const urlStatsData = JSON.parse(urlStatsMatch[1]);
            questionChat.totalUrlsBeforeFilter = urlStatsData.totalUrlsBeforeFilter;
            questionChat.totalUrlsAfterFilter = urlStatsData.totalUrlsAfterFilter;
            // console.log('📊 URL Statistics:', urlStatsData);
            setChat([...chat]);
          } catch (error) {
            console.error('Error parsing URL stats JSON:', error);
          }
        }

        // Parse date update marker
        const dateUpdateMatch = value && value.match(/__DATE_UPDATE_JSON_START__(.*?)__DATE_UPDATE_JSON_END__/s);
        if (dateUpdateMatch) {
          try {
            const { startDate, endDate, message: dateMessage } = JSON.parse(dateUpdateMatch[1]);
            const { setMultiple } = this.props.researchContext.actions;

            const { startLiteDataDate: prevStart, endLiteDataDate: prevEnd } = this.props.researchContext.state;
            const fmtDate = raw => {
              if (!raw) return null;
              const s = String(raw);
              const iso = /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s.slice(0, 10);
              const d = new Date(iso + 'T00:00:00');
              return isNaN(d) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            };
            const oldStartFmt = fmtDate(prevStart);
            const oldEndFmt = fmtDate(prevEnd);
            const oldRange = oldStartFmt && oldEndFmt ? `${oldStartFmt} – ${oldEndFmt}` : null;
            const newRange = `${fmtDate(startDate)} – ${fmtDate(endDate)}`;
            const statusHtml = oldRange && oldRange !== newRange
              ? `Date filter updated: <strong>${oldRange}</strong> → <strong>${newRange}</strong> based on your question.`
              : `Date filter updated to <strong>${newRange}</strong> based on your question.`;
            this.setState({ statusMessage: statusHtml });
            setTimeout(() => this.setState({ statusMessage: '' }), 6000);

            setMultiple({ startLiteDataDate: startDate, endLiteDataDate: endDate });
          } catch (error) {
            console.error('Error parsing date update JSON:', error);
          }
        }

        // Extract markers and their content from message
        const cleanedMessage = message
          ? message.replace(/_{2,3}SOURCES_JSON_START__(.*?)__SOURCES_JSON_END_{0,3}/gs, '')
            .replace(/_{2,3}URL_STATS_JSON_START__(.*?)__URL_STATS_JSON_END_{0,3}/gs, '')
            .replace(/_{2,3}ROUTER_JSON_START__(.*?)__ROUTER_JSON_END_{0,3}/gs, '')
            .replace(/_+DATE_UPDATE_JSON_START__.*?__DATE_UPDATE_JSON_END__/gs, '')
          : '';

        // Detect and handle status messages
        let finalCleanedMessage = cleanedMessage || '';
        if (finalCleanedMessage.includes('<span class="typing-message')) {
          const statusMatch = finalCleanedMessage.match(/<span class="typing-message[^"]*">(.*?)<\/span>/);
          if (statusMatch) {
            if (finalCleanedMessage.includes('searching-within')) {
              this.setState({ dateStatusMessage: statusMatch[1] });
            } else {
              this.setState({ statusMessage: statusMatch[1] });
            }
          }
          // Remove all status tags from this chunk
          finalCleanedMessage = finalCleanedMessage.replace(/<span class="typing-message[^"]*">.*?<\/span>\s*\n*/g, '');
        }

        // Handle DATE_UPDATE markers that are split across multiple chunks.
        // Track insideDateMarker across iterations to discard all fragments correctly.
        if (insideDateMarker) {
          if (finalCleanedMessage.includes('__DATE_UPDATE_JSON_END__')) {
            // Closing chunk: discard everything up to and including END token, keep the rest.
            finalCleanedMessage = finalCleanedMessage.replace(/^[\s\S]*?__DATE_UPDATE_JSON_END__/, '');
            insideDateMarker = false;
          } else {
            // Still inside the marker block — discard entire chunk.
            finalCleanedMessage = '';
          }
        } else if (finalCleanedMessage.includes('_DATE_UPDATE_JSON_START__') && !finalCleanedMessage.includes('__DATE_UPDATE_JSON_END__')) {
          // Opening chunk without closing token (handles 1-2 leading underscores): remove from START to end, set flag for next chunks.
          finalCleanedMessage = finalCleanedMessage.replace(/_+DATE_UPDATE_JSON_START__[\s\S]*/g, '');
          insideDateMarker = true;
        }

        if (done || stop) {
          // Finally, clean markers from text as well
          const finalText = text.replace(/_{2,3}SOURCES_JSON_START__(.*?)__SOURCES_JSON_END_{0,3}/gs, '').replace(/_{2,3}URL_STATS_JSON_START__(.*?)__URL_STATS_JSON_END_{0,3}/gs, '').replace(/_+DATE_UPDATE_JSON_START__.*?__DATE_UPDATE_JSON_END__/gs, '') + "\n";
          const updatedMessages = [...messages];
          updatedMessages[updatedMessages.length - 1] = { ...recentMessage, content: mdToHTML(finalText, false) };
          setChat(updatedMessages);

          if (getParams(this.props.location).has('debug'))
            console.log("Debug mode - skipping report quality calculation");
          break;
        }

        if (recentMessage.role === "assistant") {
          if (longReportType != "industryReport" && (expandResearch || showMore) && recentMessage.content.toLowerCase().includes("sorry")) {
            const previous = recentMessage.content.toLowerCase().split("sorry")[0].lastIndexOf("\n");
            recentMessage.content = (previous != -1 ? recentMessage.content.slice(0, previous) : "I expanded my search, but I could not find anything. Please contact erhan@enkiai.com to run a deep Enki search.");
            setChat(messages);
            break;
          } else if (longReportType != "industryReport" && retry > 0 && recentMessage.content.toLowerCase().includes("sorry")) {
            const previous = recentMessage.content.toLowerCase().split("sorry")[0].lastIndexOf("\n");
            const newContent = (previous != -1 ? recentMessage.content.slice(0, previous) : "Sorry, I don't have enough data to answer.");
            const updatedMessages = [...messages];
            updatedMessages[updatedMessages.length - 1] = { ...recentMessage, content: newContent };
            setChat(updatedMessages);
            break;
          } else if (longReportType != "industryReport" && recentMessage.content.toLowerCase().includes("sorry")) {
            console.error("Aborting due to 'sorry' in response");
            throw new Error('Message includes sorry.');
          } else {
            // Only append content and update chat if we have real content
            if (finalCleanedMessage.trim()) {
              text += finalCleanedMessage;
              // Safety net: clean text itself so cross-chunk partial markers are removed before rendering
              text = text.replace(/_+DATE_UPDATE_JSON_START__.*?__DATE_UPDATE_JSON_END__/gs, '');
              const cleanedText = text.replace(/_{2,3}SOURCES_JSON_START__(.*?)__SOURCES_JSON_END_{0,3}/gs, '').replace(/_{2,3}URL_STATS_JSON_START__(.*?)__URL_STATS_JSON_END_{0,3}/gs, '');
              const updatedMessages = [...messages];
              updatedMessages[updatedMessages.length - 1] = { ...recentMessage, content: mdToHTML(cleanedText, false) };
              setChat(updatedMessages);
            }
          }
        } else {
          // If we have actual content (not just status tags), transition from typing to assistant message
          if (finalCleanedMessage.trim()) {
            text += finalCleanedMessage;
            // Safety net: clean text itself so cross-chunk partial markers are removed before rendering
            text = text.replace(/_+DATE_UPDATE_JSON_START__.*?__DATE_UPDATE_JSON_END__/gs, '');
            const cleanedText = text.replace(/_{2,3}SOURCES_JSON_START__(.*?)__SOURCES_JSON_END_{0,3}/gs, '').replace(/_{2,3}URL_STATS_JSON_START__(.*?)__URL_STATS_JSON_END_{0,3}/gs, '');
            setTyping(false);
            setExpandingTyping(false);
            this.setState({ statusMessage: null, dateStatusMessage: null }); // Clear status when content starts
            setChat([...chat, { role: "assistant", content: mdToHTML(cleanedText, false) }]);
          }
        }
      }

      // Disable this feature for now
      // const lastMessage = this.state.chat[this.state.chat.length - 1];
      // if(!lastMessage.content.includes("<a")){
      //   throw 'Response does not contain link.';
      // }
      // if(lastMessage.content.includes("knowledge cutoff date")){
      //   throw 'Response contains stop words.';
      // }


      this.props.researchContext.actions.setStop(false);
      this.props.researchContext.actions.setStreaming(false);
      this.props.researchContext.actions.generateFollowUpQuestions();

    } catch (e) {
      console.error("Error in getAnswer:", e);
      if (e instanceof DOMException && e.name == "AbortError") {
        this.props.researchContext.actions.setStop(false);
        this.props.researchContext.actions.setStreaming(false);
        return
      }

      const { chat } = this.props.researchContext.state;
      const messages = chat;
      const recentMessage = messages.length > 0 ? messages[messages.length - 1] : null;

      if (!recentMessage) {
        // Chat state is unexpectedly empty (e.g. reset mid-stream by a race
        // with another request). Bail out safely instead of throwing again,
        // which would leave `streaming`/`typing` stuck true forever and hide
        // the Presentation/Explore Charts buttons for the rest of the session.
        setTyping(false);
        setStreaming(false);
        return;
      }

      if (recentMessage.role === "assistant") {
        setTyping(true);
        setChat(messages.slice(0, -1));
      } else {
        setTyping(true);
      }

      await delay(1000);
      this.getAnswer(message, chat, expandResearch, showMore, retry + 1);
    }
  }

  render() {
    const { mobileNavigationStatus, currentUserData = {}, researchContext, location } = this.props;
    const debug = getParams(location).has('debug');
    const { state, actions } = researchContext;
    const {
      chat = [],
      imagesLoading,
      reportDescriptions,
      shortReportDescriptions,
      typing,
      expandingTyping,
      streaming,
      autocomplete,
      conversationHistory,
      showConversationHistory,
      longReportType,
      startLiteDataDate, endLiteDataDate, model, inputSuggestions,
      enhanceSuggestions, showEnhanceOptions, enhanceLoading,
      message,
      systemPrompt,
      reportPrompt,
      keywordPrompt,
      historyLoading,
    } = state;
    const { userType = 'demo' } = currentUserData;
    const autocompleteSuggestions = (chat.length > 0 ? ["<b>Past Questions</b>"] : []).concat([...new Set(chat
      .filter(i => i && i.role == "user")
      .map(i => i.content.trim())
    )]);

    return (
      <div className={`margin-t-0 sidenav research-sidenav ${mobileNavigationStatus ? 'open' : ''}`}>
        <ResearchNavBar
          userType={userType}
          startLiteDataDate={startLiteDataDate}
          endLiteDataDate={endLiteDataDate}
        />
        <div className="research">
          <div className="research__form">
            {chat.length === 0
              ? <div>
                <div className="research__head__title">Ask Enki to uncover cleantech opportunities</div>
                <div className="research__head__subtitle">Stay ahead by tracking commercial signals, emerging technologies, and market shifts.</div>
              </div> : ""}
            <div className="research__form__history-container" ref={this.chatRef}>
              {debug &&
                <div className="research__form__header">
                  <div style={{ width: "50%", paddingRight: "5px" }}>
                    System Prompt
                    <div >
                      <TextareaAutosize
                        onChange={this.onTextAreaChange("systemPrompt")}
                        value={systemPrompt || ""}
                      />
                    </div>
                  </div>
                  <div style={{ width: "50%", paddingLeft: "5px" }}>
                    Report Prompt
                    <div>
                      <TextareaAutosize
                        onChange={this.onTextAreaChange("reportPrompt")}
                        value={reportPrompt || ""}
                      />
                    </div>
                  </div>
                  <div style={{ width: "50%", paddingRight: "5px" }}>
                    Keyword Prompt
                    <div>
                      <TextareaAutosize
                        onChange={this.onTextAreaChange("keywordPrompt")}
                        value={keywordPrompt || ""}
                      />
                    </div>
                  </div>
                  <div style={{ width: "50%", paddingLeft: "5px" }}>
                    Model {this.state.modelsLoading && <span style={{ fontSize: '0.8em', color: '#888' }}>(loading...)</span>}
                    <div>
                      <select value={model} onChange={this.onSelectChange("model")}>
                        <option value="">Select a model</option>
                        {this.state.availableModels.map(m => {
                          const formatTokens = (num) => {
                            if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
                            if (num >= 1000) return `${Math.round(num / 1000)}K`;
                            return num;
                          };
                          const tokenInfo = m.inputTokenLimit && m.outputTokenLimit
                            ? ` (${formatTokens(m.inputTokenLimit)} in / ${formatTokens(m.outputTokenLimit)} out)`
                            : '';
                          return (
                            <option key={m.id} value={m.id} title={m.description}>
                              {m.name}{tokenInfo}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>}
              <ChatStreamView userProfile={currentUserData} />
              {streaming === false && imagesLoading === true &&
                <div className="typing-message-wrapper">
                  <div className="typing-message"> Give me a few mins… I’m loading charts related to your question </div>
                  <div className="typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              }

              {typing === true ? <div className={"research__form__assistant"} >
                <div className="research__form__image"><Image src={circleProfile} /></div>
                <div className="research__form__speaker">ENKI</div>
                <div className="research__form__message">
                  {this.state.dateStatusMessage && <><span className="typing-message--pinned" dangerouslySetInnerHTML={{ __html: this.state.dateStatusMessage }}></span><br /></>}
                  {(this.state.statusMessage || expandingTyping === true || longReportType === "deepReport") ?
                    <div className="typing-message" dangerouslySetInnerHTML={{ __html: this.state.statusMessage || "Give me a few mins… I’m expanding the search" }}>
                    </div> : ""}
                  <div className="typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div> : ""}

              <div className="research__form__row">
                {chat.length == 0 && <div className="research__form__description" dangerouslySetInnerHTML={{ __html: mdToHTML(reportDescriptions[longReportType]) }}></div>}
                <div className="research__form__followup-box">
                  {chat.length == 0 && <div className="research__form__short-description" dangerouslySetInnerHTML={{ __html: mdToHTML(shortReportDescriptions[longReportType]) }}></div>}
                  <FollowUpQuestionList />
                </div>
              </div>
            </div>

            <div className="research__form__input-container">
              {autocomplete && inputSuggestions.length > 0 ? <OutsideClick onOutsideClick={this.onOutsideClick}>
                <div className='research__form__autocomplete-box'>{(autocompleteSuggestions).concat(inputSuggestions).map((i, index) => <div key={index} className="research__form__autocomplete" onClick={this.onAutocompleteClick} dangerouslySetInnerHTML={{ __html: i }}></div>)}</div>
              </OutsideClick> : ""}

              {showEnhanceOptions && enhanceSuggestions.length > 0 ? <OutsideClick onOutsideClick={this.onEnhanceOutsideClick}>
                <div
                  className='research__form__autocomplete-box research__form__enhance-box'
                  role="listbox"
                  tabIndex={-1}
                  onKeyDown={(e) => { if (e.key === "Escape") this.onEnhanceOutsideClick(e); }}
                >
                  {enhanceSuggestions.map((i, index) => (
                    <div
                      key={index}
                      className="research__form__autocomplete research__form__enhance-option"
                      role="option"
                      aria-selected="false"
                      tabIndex={0}
                      onClick={this.onEnhanceOptionClick}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.onEnhanceOptionClick(e); } }}
                    >{i}</div>
                  ))}
                </div>
              </OutsideClick> : ""}

              {message && message.trim().length > 0 ?
                <div className={`research__form__icon research__form__icon--enhance${enhanceLoading ? " research__form__icon--loading" : ""}`} onClick={this.onEnhanceClick} title="Enhance this question">
                  <i className="material-icons">{enhanceLoading ? "hourglass_empty" : "auto_awesome"}</i>
                </div> : ""}

              {typing || streaming ?
                <div className="research__form__icon" onClick={this.onStopClick} > <i className="material-icons">stop_circle</i></div> :
                <div className="research__form__icon" onClick={this.sendUserMessage} > <i className="material-icons">send</i></div>
              }

              <TextareaAutosize
                rows="1"
                autoFocus
                className="research__form__input"
                onChange={this.onInputChange}
                onKeyPress={this.onKeyUp}
                value={message || ""}
                onClick={this.onInputChange}
                ref={this.inputRef}
              />
            </div>

          </div>
        </div>
        <ScrollToTop streaming={this.props.researchContext.state.streaming} />
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => {
  return {
    fetchCurrentUserData: () => dispatch(fetchCurrentUserData()),
    pushToastMessage: (toastData) => dispatch(pushToastMessage(toastData)),
    receiveCurrentUserData: (params) => dispatch(receiveCurrentUserData(params)),
    updateMessagePopup: (data) => dispatch(updateMessagePopup(data)),
    setRequestStatus: (status) => dispatch(setRequestStatus(status)),
  }
};

const mapStateToProps = function (store) {
  const { rootReducers, userReducers } = store;
  const { userDataReducers } = rootReducers;
  const { mobileNavigationReducers } = userReducers;
  return {
    currentUserData: userDataReducers.currentUserData,
    mobileNavigationStatus: mobileNavigationReducers.status
  };
};

const delay = ms => new Promise(res => setTimeout(res, ms));

export default withResearchContext(connect(mapStateToProps, mapDispatchToProps)(Research));
